/**
 * Feedback NPS-ish — Stage 8.
 *
 * Sources `/api/bi/feedback/nps/`. The backend uses a tiny keyword
 * classifier to bucket free-text into positive / negative / neutral
 * (no rating field in the data model yet) and returns daily counts,
 * top topics, top keywords and a recent triage feed.
 */
import { useMemo, useState } from 'react';
import {
  Sparkles,
  ThumbsUp,
  ThumbsDown,
  Hash,
  Inbox,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { KPICard } from '@/components/charts/KPICard';
import { Sparkline } from '@/components/charts/Sparkline';
import { useFeedbackNps, type FeedbackSentiment } from '@/api/bi';
import { rangeISO, useFilters } from '@/state/filters';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

const SENTIMENT_FILTERS: { id: FeedbackSentiment | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'positive', label: 'Positive' },
  { id: 'negative', label: 'Negative' },
  { id: 'neutral', label: 'Neutral' },
];

export function FeedbackPage() {
  const range = useFilters((s) => s.range);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const { start: startISO, end: endISO } = useMemo(
    () => rangeISO(range, timeRangeMode),
    [range, timeRangeMode],
  );

  const query = useFeedbackNps({ start: startISO, end: endISO });
  const data = query.data;
  const totals = data?.totals;

  const [sentiment, setSentiment] = useState<FeedbackSentiment | 'all'>('all');

  const dailyCount = useMemo(
    () => (data?.daily ?? []).map((d) => ({ value: d.count })),
    [data?.daily],
  );
  const dailyScore = useMemo(
    () => (data?.daily ?? []).map((d) => ({ value: d.score })),
    [data?.daily],
  );

  const filteredRecent = useMemo(() => {
    const rows = data?.recent ?? [];
    if (sentiment === 'all') return rows;
    return rows.filter((r) => r.sentiment === sentiment);
  }, [data?.recent, sentiment]);

  const topicMax = Math.max(1, ...(data?.topics ?? []).map((t) => t.count));

  return (
    <>
      <PageHeader
        eyebrow="Voice of Customer"
        title="Feedback"
        description="Sentiment-tagged feedback. Heuristic until the rating field lands — but already useful for spotting the ugly stuff."
        actions={
          <Badge tone="info" className="gap-1">
            <Sparkles className="h-3 w-3" /> Stage 8
          </Badge>
        }
      />

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard
          label="Feedback received"
          value={fmt.number(totals?.feedback ?? 0)}
          loading={query.isLoading}
          trend={dailyCount}
        />
        <KPICard
          label="Positive"
          value={fmt.number(totals?.positive ?? 0)}
          loading={query.isLoading}
        />
        <KPICard
          label="Negative"
          value={fmt.number(totals?.negative ?? 0)}
          goodWhenUp={false}
          loading={query.isLoading}
        />
        <KPICard
          label="NPS-ish"
          value={`${(totals?.nps_like ?? 0).toFixed(1)}`}
          loading={query.isLoading}
          trend={dailyScore}
          hint="Positive% − Negative% across classified sample. -100 to 100."
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Sentiment mix</CardTitle>
          </CardHeader>
          <CardBody>
            {query.isLoading ? (
              <div className="h-28 animate-pulse rounded-md bg-muted/40" />
            ) : (totals?.sample ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground">No classifiable feedback yet.</p>
            ) : (
              <SentimentBars
                positive={totals?.positive ?? 0}
                negative={totals?.negative ?? 0}
                neutral={totals?.neutral ?? 0}
              />
            )}
          </CardBody>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Top topics</CardTitle>
            <Badge tone="neutral">{(data?.topics ?? []).length} matched</Badge>
          </CardHeader>
          <CardBody>
            {query.isLoading ? (
              <div className="h-28 animate-pulse rounded-md bg-muted/40" />
            ) : (data?.topics ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No clear themes yet.</p>
            ) : (
              <div className="space-y-1.5">
                {(data?.topics ?? []).slice(0, 8).map((t) => (
                  <div key={t.topic} className="flex items-center gap-2">
                    <span className="w-32 text-xs font-medium capitalize text-foreground">
                      {t.topic.replace('_', ' ')}
                    </span>
                    <div className="flex-1">
                      <div
                        className="h-2.5 rounded-full bg-primary/70"
                        style={{ width: `${(t.count / topicMax) * 100}%`, minWidth: 4 }}
                      />
                    </div>
                    <span className="w-8 text-right text-[11px] tabular-nums text-muted-foreground">
                      {t.count}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Trending keywords</CardTitle>
          <Hash className="h-3.5 w-3.5 text-muted-foreground" />
        </CardHeader>
        <CardBody>
          {query.isLoading ? (
            <div className="h-16 animate-pulse rounded-md bg-muted/40" />
          ) : (data?.keywords ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing recurring.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {(data?.keywords ?? []).map((k) => (
                <span
                  key={k.keyword}
                  className={cn(
                    'inline-flex items-baseline gap-1 rounded-full px-2.5 py-1 text-xs',
                    k.tone === 'positive'
                      ? 'bg-success/10 text-success ring-1 ring-success/30'
                      : 'bg-danger/10 text-danger ring-1 ring-danger/30',
                  )}
                >
                  <span className="font-medium">{k.keyword}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">×{k.count}</span>
                </span>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Recent feedback</CardTitle>
          <div className="flex items-center gap-1">
            {SENTIMENT_FILTERS.map((s) => (
              <button
                key={s.id}
                onClick={() => setSentiment(s.id)}
                className={cn(
                  'press rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors',
                  sentiment === s.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/70',
                )}
              >
                {s.label}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardBody>
          {query.isError ? (
            <ErrorState description="Couldn't load feedback." onRetry={() => void query.refetch()} />
          ) : query.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-12 animate-pulse rounded-md bg-muted/40" />
              ))}
            </div>
          ) : filteredRecent.length === 0 ? (
            <div className="flex flex-col items-center gap-1 py-8 text-center text-sm text-muted-foreground">
              <Inbox className="h-5 w-5" />
              <span>No feedback in this slice.</span>
            </div>
          ) : (
            <ul className="space-y-2">
              {filteredRecent.map((r) => (
                <li
                  key={r.id}
                  className="flex items-start gap-3 rounded-md border border-border bg-surface px-3 py-2"
                >
                  <SentimentChip tone={r.sentiment} />
                  <div className="flex-1 text-sm text-foreground">{r.snippet || '—'}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {r.created_at ? fmt.ago(r.created_at) : ''}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </>
  );
}

function SentimentBars({
  positive,
  negative,
  neutral,
}: {
  positive: number;
  negative: number;
  neutral: number;
}) {
  const total = Math.max(1, positive + negative + neutral);
  return (
    <div className="space-y-2">
      <Row icon={<ThumbsUp className="h-3.5 w-3.5 text-success" />} label="Positive" count={positive} total={total} tone="success" />
      <Row icon={<ThumbsDown className="h-3.5 w-3.5 text-danger" />} label="Negative" count={negative} total={total} tone="danger" />
      <Row icon={<Inbox className="h-3.5 w-3.5 text-muted-foreground" />} label="Neutral" count={neutral} total={total} tone="muted" />
    </div>
  );
}

function Row({
  icon,
  label,
  count,
  total,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  total: number;
  tone: 'success' | 'danger' | 'muted';
}) {
  const pct = (count / total) * 100;
  const fill =
    tone === 'success'
      ? 'bg-success'
      : tone === 'danger'
        ? 'bg-danger'
        : 'bg-muted-foreground/40';
  return (
    <div className="flex items-center gap-2">
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground">
        {icon} {label}
      </span>
      <div className="flex-1">
        <div className={cn('h-2.5 rounded-full', fill)} style={{ width: `${pct}%`, minWidth: 4 }} />
      </div>
      <span className="w-16 text-right text-xs tabular-nums text-muted-foreground">
        {fmt.number(count)} ({pct.toFixed(0)}%)
      </span>
    </div>
  );
}

function SentimentChip({ tone }: { tone: FeedbackSentiment }) {
  if (tone === 'positive') return <Badge tone="success">positive</Badge>;
  if (tone === 'negative') return <Badge tone="danger">negative</Badge>;
  return <Badge tone="neutral">neutral</Badge>;
}

// Reserved for the lint suppressor (Sparkline import used conditionally above).
export const _spark = Sparkline;
